import React, { useState, useEffect } from 'react'
import {
  Button,
  ButtonProps,
  Position,
  Classes,
  PopoverInteractionKind,
  Tag,
} from '@blueprintjs/core'
import { Popover } from '@ui'
import { Flex, Avatar, EmployeeSpaceAssignment } from '.'
import { CatalogEmployeeFragment } from '@api/fragments'
import { ICatalogEmployee } from '@api/types'
import client from '../../stores/apollo'
import gql from 'graphql-tag'
import { navigateWithBasepath as navigate } from '../../App'
import css from './employeeWithDetailsPopover.module.scss'

interface IEmployee {
  id: string
  name: string
}

interface EmployeeWithDetailsPopoverProps {
  employee: IEmployee
  buttonProps?: ButtonProps
}

export default function EmployeeWithDetailsPopover({
  employee,
  buttonProps,
}: EmployeeWithDetailsPopoverProps) {
  return (
    <Popover
      minimal
      autoFocus={false}
      interactionKind={PopoverInteractionKind.HOVER}
      position={Position.BOTTOM}
      modifiers={{ arrow: { enabled: false } }}
      content={
        <div style={{ padding: 10 }}>
          <EmployeeCard employee={employee} />
        </div>
      }
    >
      <Button className={css.popoverbutton} {...buttonProps}>
        {employee.name}
      </Button>
    </Popover>
  )
}

export function EmployeeCard({ employee }: { employee: IEmployee }) {
  const details = useEmployeeDetails(employee.id)

  return details ? (
    <Flex align="center" style={{ position: 'relative' }}>
      <Flex align="center" style={{ marginRight: 20 }}>
        <Avatar name={details.name} assets={details.assets} size={Avatar.Size.SM} />
        <div style={{ marginLeft: 10, paddingTop: 5 }}>
          <div
            className={css.name__link}
            onClick={() => navigate(`?employeeId=${employee.id}`, {})}
          >
            {details.name}
          </div>
          <div style={{ fontSize: 14, color: 'var(--grey1)' }}>{details.email}</div>
        </div>
      </Flex>
      <EmployeeSpaceAssignment {...details} />
    </Flex>
  ) : (
    <GhostCard />
  )
}

function GhostCard() {
  return (
    <Flex align="center">
      <Flex align="center" style={{ marginRight: 20 }}>
        <Avatar skeleton size={Avatar.Size.SM} />
        <div style={{ marginLeft: 10 }}>
          <div style={{ fontSize: 16, marginBottom: 4, width: 140 }} className={Classes.SKELETON}>
            name
          </div>
          <div style={{ fontSize: 14, width: 200 }} className={Classes.SKELETON}>
            email
          </div>
        </div>
      </Flex>
      <Tag minimal round className={Classes.SKELETON}>
        Placeholder
      </Tag>
    </Flex>
  )
}

let persistedState: ICatalogEmployee | null = null

const query = gql`
  query employeeCard($id: ID!, $assignmentPlanId: ID) {
    employee: node(id: $id) {
      ...Employee
    }
  }
  ${CatalogEmployeeFragment}
`

function useEmployeeDetails(id: string): ICatalogEmployee | null {
  const [details, setDetails] = useState<ICatalogEmployee | null>(
    persistedState && persistedState.id === id ? persistedState : null
  )

  useEffect(() => {
    client.gql
      .query({ query, variables: { id, assignmentPlanId: null } })
      .then(({ data }: { data: { employee: ICatalogEmployee } }) => setDetails(data.employee))
  }, [id])

  useEffect(() => {
    persistedState = details
  })

  return details
}
